import { defineStore } from "pinia";
import { ref } from "vue";
import policyService from "@/services/hrm/policyService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePolicyStore = defineStore("policy", () => {
  const policies = ref([]);
  const policy = ref(null);
  const loading = ref(false);
  const error = ref(null);

  async function fetchPolicies(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      policies.value = [];
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const res = await policyService.getAll(params);
      policies.value = res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch policies";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchPolicy(id) {
    loading.value = true;
    error.value = null;
    try {
      const res = await policyService.get(id);
      policy.value = res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch policy";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function createPolicy(data) {
    loading.value = true;
    error.value = null;
    try {
      const res = await policyService.create(data);
      toast("Policy created successfully");
      await fetchPolicies();
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to create policy";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updatePolicy(id, data) {
    loading.value = true;
    error.value = null;
    try {
      const res = await policyService.update(id, data);
      toast("Policy updated successfully");
      await fetchPolicies();
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update policy";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deletePolicy(id) {
    loading.value = true;
    error.value = null;
    try {
      await policyService.delete(id);
      toast("Policy deleted successfully");
      await fetchPolicies();
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to delete policy";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    error.value = null;
    try {
      await policyService.bulkDelete(ids);
      toast("Policies deleted successfully");
      await fetchPolicies();
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to bulk delete";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function downloadReport(format = "xlsx", params = {}) {
    try {
      const res = await policyService.exportReport(format, params);
      const mime =
        format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ext = format === "pdf" ? "pdf" : "xlsx";
      link.href = url;
      link.setAttribute(
        "download",
        `policy-report-${new Date().toISOString().slice(0, 10)}.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to export policies";
      toast(msg, "error");
      throw err;
    }
  }

  return {
    policies,
    policy,
    loading,
    error,
    fetchPolicies,
    fetchPolicy,
    createPolicy,
    updatePolicy,
    deletePolicy,
    bulkDelete,
    downloadReport,
  };
});
